import usePaginator from '~/composables/usePaginator';
import { listPaymentSlips } from '~/services/apiPaymentSlips';
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from './useAuthStore';
import { PAYMENT_SLIP_FILTERS } from '~/helpers/PaymentSlips.const';
import { PAGINATION } from '~/helpers/constants';

export const usePaymentSlipStore = defineStore(
  'MY_SCAN_SOURCE_PAYMENT_SLIP_STORE',
  () => {
    const AUTH_STORE = useAuthStore();
    const { session } = storeToRefs(AUTH_STORE);

    const bankSlipListPaginator = usePaginator();
    const { pagination: paymentSlipListPagination, CallPaginate: CallPaymentSlipListPaginate } =
      bankSlipListPaginator;

    const expiration = ref<Date | null>(null);
    const loadingPaymentSlips = ref(false);
    const paymentSlipListFilters = ref({ ...PAYMENT_SLIP_FILTERS });
    const paymentSlipList = ref<any[]>([]);
    const paymentSlip = ref({});
    const noDataFiltered = ref(false);
    const hasFilteredData = computed(() =>
      noDataFiltered.value ? 'TOAST.DETAILS.NO_DATA_FILTERED' : '',
    );

    const isExpired = computed(() => {
      const validator = new Date().getTime();
      if (!expiration.value || typeof expiration.value.getTime !== 'function') return true;
      return expiration.value.getTime() <= validator;
    });

    function setExpirationTime() {
      const expirationDate = new Date();
      expirationDate.setMinutes(expirationDate.getMinutes() + 3);
      expiration.value = expirationDate;
    }

    function setPaymentSlipList(data: { paymentSlipList: any[]; pagination: typeof PAGINATION }) {
      paymentSlipList.value = data.paymentSlipList;
      bankSlipListPaginator.CallSetPagination(data.pagination);
    }

    function resetFilters() {
      paymentSlipListFilters.value = { ...PAYMENT_SLIP_FILTERS };
    }

    async function dispatchGetPaymentSlips(force = false): Promise<void> {
      loadingPaymentSlips.value = true;
      if (!isExpired.value && !force) {
        loadingPaymentSlips.value = false;
        return;
      }

      if (!session.value?.currentEconomicGroupMember) {
        loadingPaymentSlips.value = false;
        throw new Error('No economic group member found in session');
      }

      await listPaymentSlips(session.value.currentEconomicGroupMember).then(
        (data) => {
          setPaymentSlipList(data);
          setExpirationTime();
          loadingPaymentSlips.value = false;
        },
        (error) => {
          setPaymentSlipList({
            paymentSlipList: [],
            pagination: { ...PAGINATION },
          });
          loadingPaymentSlips.value = false;
          throw error;
        },
      );
    }

    function $reset() {
      paymentSlipList.value = [];
      paymentSlip.value = {};
      paymentSlipListFilters.value = { ...PAYMENT_SLIP_FILTERS };
      loadingPaymentSlips.value = false;
      expiration.value = null;
      noDataFiltered.value = false;
      bankSlipListPaginator.CallSetPagination({ ...PAGINATION });
    }

    return {
      paymentSlipListFilters,
      paymentSlipList,
      paymentSlip,
      paymentSlipListPagination,
      hasFilteredData,
      noDataFiltered,
      loadingPaymentSlips,
      isExpired,
      expiration,
      CallPaymentSlipListPaginate,
      dispatchGetPaymentSlips,
      resetFilters,
      setPaymentSlipList,
      setExpirationTime,
      onSessionOpen: dispatchGetPaymentSlips,
      onSessionClose: $reset,
      onChangeEconomicGroup: dispatchGetPaymentSlips,
      $reset,
    };
  },
  {
    persist: true,
  },
);
